/**
 * Excel export dropdown for report pages.
 * Three modes: global (one sheet), by district, by region.
 */

import { useState } from "react";
import { ChevronDown, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import {
  buildExcelWorkbook,
  downloadWorkbook,
  type ExportMode,
  type ExportColumn,
} from "../utils/exportExcel";

interface ExportDropdownProps<T> {
  rows: T[];
  isFiltered: boolean;
  /** Base filename without extension (date/year suffix already included). */
  filename: string;
  columns: ExportColumn<T>[];
  getDistrictName: (row: T) => string | null;
  getRegionName: (row: T) => string | null;
  /** Returns the name string used for A→Z sorting of rows in the export. */
  getSortName: (row: T) => string;
  disabled?: boolean;
}

export function ExportDropdown<T>({
  rows,
  isFiltered,
  filename,
  columns,
  getDistrictName,
  getRegionName,
  getSortName,
  disabled,
}: ExportDropdownProps<T>) {
  const { toast } = useToast();
  const [exporting, setExporting] = useState<ExportMode | null>(null);

  const doExport = async (mode: ExportMode) => {
    if (!rows.length) {
      toast({
        title: "Sin datos",
        description: "No hay registros para exportar",
        variant: "destructive",
      });
      return;
    }
    setExporting(mode);
    try {
      const groupLabel = mode === "by-district" ? "Distrito" : "Región";
      const getGroupName = mode === "by-district" ? getDistrictName : getRegionName;
      const sorted = [...rows].sort((a, b) =>
        getSortName(a).localeCompare(getSortName(b), "es", { sensitivity: "base" })
      );

      const wb = await buildExcelWorkbook({
        mode,
        rows: sorted,
        columns,
        getGroupName: mode === "global" ? () => null : getGroupName,
        groupLabel,
        // For by-region, supply the district getter so tabs are ordered and
        // colored by parent district.
        getParentGroupName: mode === "by-region" ? getDistrictName : undefined,
      });

      const filteredSuffix = isFiltered ? "_filtrado" : "";
      await downloadWorkbook(wb, `${filename}${filteredSuffix}`);
    } catch (err) {
      console.error("Export failed:", err);
      toast({
        title: "Error al exportar",
        description: "No se pudo generar el archivo Excel",
        variant: "destructive",
      });
    } finally {
      setExporting(null);
    }
  };

  const isLoading = exporting !== null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled || isLoading}>
          {isLoading ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <Download className="mr-2 size-4" />
          )}
          Excel
          <ChevronDown className="ml-1 size-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel className="text-xs">Exportar a Excel</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => doExport("global")} disabled={isLoading}>
          Todo en una hoja
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => doExport("by-district")} disabled={isLoading}>
          Por Distrito
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => doExport("by-region")} disabled={isLoading}>
          Por Región
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
